/**
 * Copyright (c) HashiCorp, Inc.
 * SPDX-License-Identifier: BUSL-1.1
 */

import {
  HdsSegmentedGroup,
  HdsFormTextInputField,
  HdsTextBody,
  HdsTag,
  HdsTooltipButton,
  HdsIcon,
  HdsButton,
} from '@hashicorp/design-system-components/components';
import Component from '@glimmer/component';
import { on } from '@ember/modifier';
import { fn } from '@ember/helper';
import { type Filter } from '../../utils/filters.ts';
import './filter-bar.scss';

export interface FilterFieldDefinition {
  name: string;
  label: string;
  type:
    | 'text'
    | 'single-select'
    | 'multi-select'
    | 'lookback'
    | 'list'
    | 'search';
  options?: { name: string; value: string }[];
}

export interface FilterBarSignature {
  Args: {
    onFiltersApplied: (filters: Filter[]) => void;
    appliedFilters?: Filter[];
    filterFieldDefinitions: FilterFieldDefinition[];
  };

  Blocks: {
    default: [];
  };
  Element: HTMLElement;
}

export default class FilterBar extends Component<FilterBarSignature> {
  updateFilters = (filters: Record<string, Filter>) => {
    this.args.onFiltersApplied(Object.values(filters));
  };

  handleClearFilters = () => {
    this.updateFilters({});
  };

  handleDismissFilter = (key: string) => {
    const updated = { ...this.appliedFilters };
    delete updated[key];
    this.updateFilters(updated);
  };

  handleRadioChange = (name: string, value: string) => {
    this.updateFilters({
      ...this.appliedFilters,
      [name]: {
        operator: '=',
        value: {
          type: 'string',
          value,
        },
        field: name,
      },
    });
  };

  handleCheckboxChange = (name: string, value: string, event: Event) => {
    const target = event.target as HTMLInputElement;
    const { checked } = target;
    const appliedFilter = this.appliedFilters[name];
    const rawValue = appliedFilter?.value?.value;
    let values = new Set<string>();
    if (Array.isArray(rawValue)) {
      values = new Set(rawValue.map((v) => `${v}`));
    } else if (typeof rawValue === 'string') {
      values.add(`${rawValue}`);
    }

    if (checked) {
      values.add(value);
    } else {
      values.delete(value);
    }
    if (values.size === 0) {
      // If no values are left, remove the filter
      const updatedFilters = { ...this.appliedFilters };
      delete updatedFilters[name];
      this.updateFilters(updatedFilters);
      return;
    }

    this.updateFilters({
      ...this.appliedFilters,
      [name]: {
        operator: '=',
        value: {
          type: 'list',
          value: Array.from(values),
        },
        field: name,
      },
    });
  };

  handleTextInputChange = (name: string, event: Event) => {
    const { value } = event.target as HTMLInputElement;
    this.updateFilters({
      ...this.appliedFilters,
      [name]: {
        operator: '=',
        value: {
          type: 'string',
          value,
        },
        field: name,
      },
    });
  };

  handleLookbackChange = (name: string, value: string) => {
    const days = parseInt(value, 10);
    const operator = days < 0 ? '>' : '<';
    this.updateFilters({
      ...this.appliedFilters,
      [name]: {
        operator,
        value: {
          type: 'duration',
          value,
        },
        field: name,
      },
    });
  };

  isEqual = (a: string, b: string) => {
    return a === b;
  };

  isCheckboxChecked = (name: string, value: string) => {
    const appliedFilter = this.appliedFilters[name];
    const rawValue = appliedFilter?.value?.value;
    if (!rawValue) {
      return false;
    }
    if (Array.isArray(rawValue)) {
      return rawValue.includes(value);
    }
    if (typeof rawValue === 'string') {
      return rawValue === value;
    }
    return false;
  };

  isLookbackChecked = (name: string, value: string) => {
    const appliedFilter = this.appliedFilters[name];
    if (!appliedFilter) {
      return false;
    } else {
      return appliedFilter.value.value === value;
    }
  };

  getValue = (name: string) => {
    return (this.appliedFilters?.[name]?.value?.value as string) ?? '';
  };

  getOperator = (name: string) => {
    return this.appliedFilters?.[name]?.operator ?? '';
  };

  friendlyAppliedString = (appliedFilter: Filter) => {
    const { operator, value: valueDefinition } = appliedFilter;
    const field = this.args.filterFieldDefinitions.find(
      (f) => f.name === appliedFilter.field,
    );

    const label = field?.label || appliedFilter.field;
    const { value } = valueDefinition;
    const { type = 'string' } = valueDefinition;
    let formattedValue = value as string;
    if (type === 'duration' && !Array.isArray(value)) {
      formattedValue = value as string;
    }
    if (type === 'list' && Array.isArray(value)) {
      formattedValue = value.join(', ');
    }

    return `${label} ${operator} ${formattedValue}`;
  };

  get appliedFilters() {
    const applied = (this.args.appliedFilters || []).reduce(
      (acc, filter) => {
        const { field } = filter;
        acc[field] = filter;
        return acc;
      },
      {} as Record<string, Filter>,
    );

    return applied;
  }

  get appliedFilterTags() {
    return Object.entries(this.appliedFilters).map(([key, filter]) => ({
      key,
      text: this.friendlyAppliedString(filter),
    }));
  }

  get appliedFiltersCount() {
    return Object.keys(this.appliedFilters).length;
  }

  get hasAppliedFilters() {
    return this.appliedFiltersCount > 0;
  }

  <template>
    <HdsSegmentedGroup class="filter-bar__filters" as |SG|>
      {{#each @filterFieldDefinitions as |filter|}}
        {{#if (this.isEqual filter.type "search")}}
          <HdsFormTextInputField
            data-test-vault-reporting-filter-bar-search={{filter.name}}
            @type="search"
            name={{filter.name}}
            placeholder="Search"
            @value={{this.getValue filter.name}}
            {{on "change" (fn this.handleTextInputChange filter.name)}}
          />
        {{else}}
          <SG.Dropdown
            data-test-vault-reporting-filter-bar-dropdown={{filter.name}}
            @listPosition="bottom-left"
            as |D|
          >
            <D.ToggleButton @color="secondary" @text={{filter.label}} />

            {{#if (this.isEqual filter.type "lookback")}}
              {{#each filter.options as |option|}}
                <D.Radio
                  name={{filter.name}}
                  @value={{option.value}}
                  checked={{this.isLookbackChecked filter.name option.value}}
                  {{on
                    "change"
                    (fn this.handleLookbackChange filter.name option.value)
                  }}
                >{{option.name}}</D.Radio>
              {{/each}}
            {{else if (this.isEqual filter.type "text")}}
              <D.Generic class="filter-bar__filters__text-filter-wrapper">
                <HdsFormTextInputField
                  @type="text"
                  name={{filter.name}}
                  @value={{this.getValue filter.name}}
                  {{on "change" (fn this.handleTextInputChange filter.name)}}
                  as |F|
                >
                  <F.Label>
                    {{filter.label}}
                  </F.Label>
                </HdsFormTextInputField>
              </D.Generic>
            {{else if (this.isEqual filter.type "single-select")}}
              {{#each filter.options as |option|}}
                <D.Radio
                  name={{filter.name}}
                  @value={{option.value}}
                  checked={{this.isEqual
                    (this.getValue filter.name)
                    option.value
                  }}
                  {{on
                    "change"
                    (fn this.handleRadioChange filter.name option.value)
                  }}
                >{{option.name}}</D.Radio>
              {{/each}}
            {{else if (this.isEqual filter.type "multi-select")}}
              {{#each filter.options as |option|}}
                <D.Checkbox
                  name={{filter.name}}
                  @value={{option.value}}
                  checked={{this.isCheckboxChecked filter.name option.value}}
                  {{on
                    "change"
                    (fn this.handleCheckboxChange filter.name option.value)
                  }}
                >{{option.name}}</D.Checkbox>
              {{/each}}
            {{/if}}
          </SG.Dropdown>
        {{/if}}
      {{/each}}
    </HdsSegmentedGroup>
    <div class="filter-bar__applied-filters">
      {{#if this.hasAppliedFilters}}
        <HdsTextBody @color="strong" @size="200" @weight="semibold">Applied
          filters ({{this.appliedFiltersCount}})</HdsTextBody>
        {{#each this.appliedFilterTags as |appliedFilter|}}
          <HdsTag
            data-test-vault-reporting-filter-bar-applied-tag={{appliedFilter.key}}
            @text={{appliedFilter.text}}
            @tooltipPlacement="right"
            @onDismiss={{fn this.handleDismissFilter appliedFilter.key}}
          />
        {{/each}}
        <HdsButton
          @size="medium"
          class="filter-bar__applied-filters__clear-button"
          @text="Clear all"
          @isInline={{true}}
          @color="tertiary"
          @icon="x"
          {{on "click" this.handleClearFilters}}
        />
      {{else}}
        <HdsTextBody
          @color="strong"
          @size="200"
          class="filter-bar__applied-filters-zero-state"
        >No filters applied&nbsp;
          <HdsTooltipButton
            @text="Use the filter bar to refine your results."
            aria-label="More information"
          >
            <HdsIcon @name="info" />
          </HdsTooltipButton></HdsTextBody>
      {{/if}}

    </div>
  </template>
}
